import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  ArrowLeft, 
  Plus, 
  Bell,
  Calendar,
  Check,
  Trash2,
  Edit2,
  Loader2,
  AlertCircle,
  Clock,
  CreditCard,
  Zap,
  Home,
  Wifi,
  Phone,
  Car,
  Shield,
  MoreHorizontal
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILL_CATEGORIES = [
  { value: "utilities", label: "Utilities", icon: Zap, color: "text-yellow-500" },
  { value: "rent", label: "Rent/Mortgage", icon: Home, color: "text-tamba-blue" },
  { value: "internet", label: "Internet/Cable", icon: Wifi, color: "text-purple-500" },
  { value: "phone", label: "Phone", icon: Phone, color: "text-green-500" },
  { value: "insurance", label: "Insurance", icon: Shield, color: "text-tamba-teal" },
  { value: "car", label: "Car Payment", icon: Car, color: "text-tamba-coral" },
  { value: "subscription", label: "Subscription", icon: CreditCard, color: "text-pink-500" },
  { value: "other", label: "Other", icon: MoreHorizontal, color: "text-gray-500" }
];

const BillRemindersPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    amount: "",
    category: "utilities",
    due_day: "1",
    frequency: "monthly",
    is_autopay: false,
    notes: ""
  });

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchBills(userData.id);
  }, [navigate]);

  const fetchBills = async (userId) => {
    try {
      const response = await axios.get(`${API}/bills/${userId}`);
      setBills(response.data.bills || []);
    } catch (error) {
      console.error("Failed to fetch bills:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.amount) {
      toast.error("Please fill in bill name and amount");
      return;
    }

    setSaving(true);
    try {
      const billData = {
        user_id: user.id,
        bill: {
          name: formData.name,
          amount: parseFloat(formData.amount),
          category: formData.category,
          due_day: parseInt(formData.due_day),
          frequency: formData.frequency,
          is_autopay: formData.is_autopay,
          notes: formData.notes
        }
      };

      if (editingBill) {
        await axios.put(`${API}/bills/${editingBill.id}`, billData);
        toast.success("Bill updated!");
      } else {
        await axios.post(`${API}/bills`, billData);
        toast.success("Bill reminder added!");
      }

      setDialogOpen(false);
      resetForm();
      fetchBills(user.id);
    } catch (error) {
      toast.error("Failed to save bill");
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (billId) => {
    try {
      await axios.post(`${API}/bills/${billId}/mark-paid`);
      toast.success("Bill marked as paid!");
      fetchBills(user.id);
    } catch (error) {
      toast.error("Failed to mark bill as paid");
    }
  };

  const handleDelete = async (billId) => {
    try {
      await axios.delete(`${API}/bills/${billId}`);
      toast.success("Bill deleted");
      fetchBills(user.id);
    } catch (error) {
      toast.error("Failed to delete bill");
    }
  };

  const handleEdit = (bill) => {
    setEditingBill(bill);
    setFormData({
      name: bill.name,
      amount: bill.amount.toString(),
      category: bill.category,
      due_day: bill.due_day.toString(),
      frequency: bill.frequency,
      is_autopay: bill.is_autopay,
      notes: bill.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingBill(null);
    setFormData({
      name: "",
      amount: "",
      category: "utilities",
      due_day: "1",
      frequency: "monthly",
      is_autopay: false,
      notes: ""
    });
  };

  const currencySymbol = user?.currency_symbol || "$";
  const totalMonthly = bills.reduce((sum, b) => sum + (b.frequency === "monthly" ? b.amount : 0), 0);
  const dueSoonBills = bills.filter(b => b.days_until_due <= 7 && !b.paid_this_month);
  const overdueBills = bills.filter(b => b.is_overdue);

  const getCategoryIcon = (category) => {
    const cat = BILL_CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : MoreHorizontal;
  };

  const getCategoryColor = (category) => {
    const cat = BILL_CATEGORIES.find(c => c.value === category);
    return cat ? cat.color : "text-gray-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl font-semibold">Bill Reminders</h1>
              <p className="text-xs text-muted-foreground">Never miss a payment</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="rounded-full bg-tamba-blue hover:bg-tamba-blue/90 gap-1">
                <Plus className="h-4 w-4" />
                Add Bill
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingBill ? "Edit Bill" : "Add New Bill"}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Bill Name</Label>
                  <Input
                    placeholder="e.g., Electric Bill"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Amount ({currencySymbol})</Label>
                    <Input
                      type="number"
                      placeholder="100"
                      value={formData.amount}
                      onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Due Day</Label>
                    <Select value={formData.due_day} onValueChange={(v) => setFormData(prev => ({ ...prev, due_day: v }))}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(31)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            {i + 1}{i === 0 ? "st" : i === 1 ? "nd" : i === 2 ? "rd" : "th"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {BILL_CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <span className="flex items-center gap-2">
                            <cat.icon className={`h-4 w-4 ${cat.color}`} />
                            {cat.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center justify-between p-3 bg-secondary/50 rounded-lg">
                  <div>
                    <Label>Auto-pay Enabled</Label>
                    <p className="text-xs text-muted-foreground">Bill is paid automatically</p>
                  </div>
                  <Switch
                    checked={formData.is_autopay}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_autopay: checked }))}
                  />
                </div>

                <div>
                  <Label>Notes (optional)</Label>
                  <Input
                    placeholder="Add any notes..."
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                <Button onClick={handleSubmit} disabled={saving} className="w-full bg-tamba-blue hover:bg-tamba-blue/90">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editingBill ? "Update Bill" : "Add Bill"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-3">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-tamba-blue/30 bg-gradient-to-br from-tamba-blue/5 to-transparent">
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Monthly Bills</p>
                <p className="text-2xl font-bold text-tamba-blue">{currencySymbol}{totalMonthly.toFixed(0)}</p>
                <p className="text-xs text-muted-foreground">{bills.length} bills tracked</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className={`border-${dueSoonBills.length > 0 ? "tamba-coral" : "tamba-teal"}/30 bg-gradient-to-br from-${dueSoonBills.length > 0 ? "tamba-coral" : "tamba-teal"}/5 to-transparent`}>
              <CardContent className="p-4">
                <p className="text-xs text-muted-foreground">Due Soon</p>
                <p className={`text-2xl font-bold ${dueSoonBills.length > 0 ? "text-tamba-coral" : "text-tamba-teal"}`}>
                  {dueSoonBills.length}
                </p>
                <p className="text-xs text-muted-foreground">within 7 days</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Due Soon Alert */}
        {dueSoonBills.length > 0 && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-tamba-coral/50 bg-tamba-coral/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-tamba-coral flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-tamba-coral">Bills Due Soon</p>
                    <div className="mt-2 space-y-1">
                      {dueSoonBills.slice(0, 3).map(bill => (
                        <div key={bill.id} className="flex items-center justify-between text-sm">
                          <span>{bill.name}</span>
                          <span className="font-medium">
                            {bill.days_until_due === 0 ? "Today!" : `${bill.days_until_due} days`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Bills List */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <h2 className="font-heading text-lg font-semibold mb-4">Your Bills</h2>
          
          {bills.length === 0 ? (
            <Card className="border-dashed border-2 border-border/50">
              <CardContent className="py-12 text-center">
                <Bell className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-semibold mb-2">No Bills Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Add your recurring bills to get reminders before they're due
                </p>
                <Button onClick={() => setDialogOpen(true)} className="bg-tamba-blue hover:bg-tamba-blue/90">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Bill
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              <AnimatePresence>
                {bills.map((bill, index) => {
                  const CategoryIcon = getCategoryIcon(bill.category);
                  const isDueSoon = bill.days_until_due <= 3;
                  const isPaid = bill.paid_this_month;
                  
                  return (
                    <motion.div
                      key={bill.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 10 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className={`border-border/50 ${isDueSoon && !isPaid ? "border-tamba-coral/50" : ""} ${isPaid ? "opacity-60" : ""}`}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-xl bg-secondary flex items-center justify-center`}>
                              <CategoryIcon className={`h-5 w-5 ${getCategoryColor(bill.category)}`} />
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <p className={`font-medium ${isPaid ? "line-through" : ""}`}>{bill.name}</p>
                                {bill.is_autopay && (
                                  <span className="text-xs px-2 py-0.5 bg-tamba-teal/10 text-tamba-teal rounded-full">
                                    Auto
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span>Due {bill.due_day}{bill.due_day === 1 ? "st" : bill.due_day === 2 ? "nd" : bill.due_day === 3 ? "rd" : "th"}</span>
                                {!isPaid && (
                                  <span className={isDueSoon ? "text-tamba-coral font-medium" : ""}>
                                    • {bill.days_until_due === 0 ? "Today!" : `${bill.days_until_due} days`}
                                  </span>
                                )}
                                {isPaid && <span className="text-tamba-teal">• Paid</span>}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <p className="font-semibold">{currencySymbol}{bill.amount.toFixed(2)}</p>
                              <p className="text-xs text-muted-foreground">{bill.frequency}</p>
                            </div>

                            <div className="flex items-center gap-1">
                              {!isPaid && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleMarkPaid(bill.id)}
                                  className="h-8 w-8 text-tamba-teal hover:text-tamba-teal hover:bg-tamba-teal/10"
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleEdit(bill)}
                                className="h-8 w-8"
                              >
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(bill.id)}
                                className="h-8 w-8 text-muted-foreground hover:text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          )}
        </motion.div>

        {/* Tips Card */}
        <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/5 to-transparent">
          <CardContent className="p-4">
            <h3 className="font-medium mb-2 flex items-center gap-2">
              <Clock className="h-4 w-4 text-tamba-yellow" />
              Bill Payment Tips
            </h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Set up auto-pay for fixed bills to never miss a payment</li>
              <li>• Review bills monthly to catch any unusual charges</li>
              <li>• Mark bills as paid to track your progress</li>
              <li>• Bills reset to unpaid at the start of each month</li>
            </ul>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default BillRemindersPage;
